import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { AlertNotice, Tabs, Tab } from "shared/components/index";
import "./ExamineeStatusTabs.scss";
import PropTypes from "prop-types";

const DEFAULT_TAB = 1;

const ExamineeStatusTabs = ({
  statusSummary,
  examinees,
  setActiveTab,
  tabs,
  isNowManagedFlag,
  isEventCheckoutFlag,
  seatingChartRequiredFlag,
}) => {
  const { t } = useTranslation();
  const hasExpanded = examinees.length > 12;
  const filteredNames = hasExpanded
    ? examinees.filter((name, index) => index < 12)
    : examinees;
  const extraNames = examinees.length - 12;
  const [displayedNames, setDisplayedNames] = useState({
    filteredNames: [],
    seeMore: true,
  });

  const isWarningContentVisible = (status, isNowManagedFlag) => {
    switch (status) {
      case "unknown":
        return seatingChartRequiredFlag ? true : false;
      case "absent":
        return isNowManagedFlag ? true : false;
      case "deniedAdmission":
        return isNowManagedFlag ? true : false;
      default:
        return false;
    }
  };

  useEffect(() => {
    setDisplayedNames({
      ...displayedNames,
      filteredNames,
      seeMore: true,
    });
  }, [examinees]);

  const seeMore = () => {
    if (displayedNames.seeMore) {
      setDisplayedNames({
        filteredNames: examinees,
        seeMore: false,
      });
    }
    if (!displayedNames.seeMore) {
      setDisplayedNames({
        filteredNames,
        seeMore: true,
      });
    }
  };

  const onClickTab = (tab) => {
    if (tabs[tab - 1] === "deniedAdmission") {
      setActiveTab("denied");
    } else {
      setActiveTab(tabs[tab - 1]);
    }
  };

  const renderNoResultsMessage = (tab, displayedNames) => {
    if (tab === "unknown" && !displayedNames.filteredNames.length) {
      return (
        <>
          <AlertNotice
            message={t(
              "reviewAndCloseRoom.examineeStatus.noExaminees"
            )}
            type="info"
          />
          <AlertNotice
            className="no-border-alert"
            message={t(
              "reviewAndCloseRoom.examineeStatus.allExaminees"
            )}
            type="success"
            noBorder
          />
        </>
      );
    } else if (!displayedNames.filteredNames.length) {
      return (
        <AlertNotice
          message={t("reviewAndCloseRoom.examineeStatus.noExaminees")}
          type="info"
        />
      );
    }
  };

  return (
    <div className="examinee-status-container">
      <h4>{t("reviewAndCloseRoom.examineeStatus.title")}</h4>
      <h5>
        {isEventCheckoutFlag
          ? t("tcEventDashboard.checkout.totalAssignedNumber", {
              number: statusSummary.total,
            })
          : t(
              "reviewAndCloseRoom.examineeStatus.totalAssignedNumber",
              { number: statusSummary.total }
            )}
      </h5>
      {seatingChartRequiredFlag && (
        <Tabs defaultTab={DEFAULT_TAB} onClickAction={onClickTab}>
          {tabs.map((tab, index) => {
            return (
              <Tab
                key={index + 1}
                title={`${t(`common.tabs.studentStatus.${tab}`)} (${
                  statusSummary[tab] ? statusSummary[tab] : 0
                })`}
              >
                {displayedNames.filteredNames.length ? (
                  <>
                    {isWarningContentVisible(tab, isNowManagedFlag) && (
                      <AlertNotice
                        message={`${t(
                          `common.messages.examineeWarningStatus.${tab}`
                        )}`}
                        type="warning"
                      />
                    )}
                    <div className="summary-list-container">
                      {displayedNames.filteredNames.map((name, i) => (
                        <div
                          key={`examinee-${i}`}
                          className="list-item"
                        >{`${name.lastName}, ${name.firstName}`}</div>
                      ))}
                    </div>
                    {hasExpanded && (
                      <div className="see-more btn-link" onClick={seeMore}>
                        {displayedNames.seeMore
                          ? t("common.labels.seeXMore", {
                              number: extraNames,
                            })
                          : t("common.labels.seeLess")}
                      </div>
                    )}
                  </>
                ) : (
                  renderNoResultsMessage(tab, displayedNames)
                )}
              </Tab>
            );
          })}
        </Tabs>
      )}
    </div>
  );
};

export default ExamineeStatusTabs;

ExamineeStatusTabs.propTypes = {
  statusSummary: PropTypes.shape({
    tested: PropTypes.number,
    deniedAdmission: PropTypes.number,
    absent: PropTypes.number,
    unresolved: PropTypes.number,
    unknown: PropTypes.number,
    total: PropTypes.number,
  }).isRequired,
  eventLevelFlag: PropTypes.bool,
  examinees: PropTypes.arrayOf(
    PropTypes.shape({
      lastName: PropTypes.string,
      firstName: PropTypes.string,
    })
  ).isRequired,
  seatingChartRequiredFlag: PropTypes.bool,
};

ExamineeStatusTabs.defaultProps = {
  eventLevelFlag: false,
  seatingChartRequiredFlag: false,
};
